import React from 'react';
import styled from 'styled-components';
import { CurrencyFormat, PriceFilter, FilterGroup } from '@lt/components';
import { i18n } from '@lt/localization';

import { numToWords } from '../../../utils/stringUtils';

import { NBSP } from '../../../constants/common';

import type { IFilter } from '../types';

const FILTER_NAME = 'prices';
const FILTER_LABEL = i18n.t('filters.prices.title');

interface IFilterPrices extends IFilter {
  touristsCount: number;
}

type SegmentType = {
  id: number;
  from: number;
  to: number;
  percent: number;
};

type PricesData = {
  minPrice: number;
  maxPrice: number;
  min: number;
  max: number;
  segments: SegmentType[];
};

const FilterPrices = ({
  data,
  onChange,
  onClear,
  className = '',
  isMobile = false,
  touristsCount,
  currencyCode,
}: IFilterPrices): JSX.Element => {
  const handleChange = ({ min, max }) => {
    const diff = { minPrice: min, maxPrice: max };
    onChange({ path: FILTER_NAME, diff });
  };

  const {
    min,
    max,
    minPrice = min,
    maxPrice = max,
    segments,
  }: PricesData = data;

  const isChanged = Boolean(minPrice || maxPrice);

  return (
    <FilterGroup
      className={className}
      label={`${FILTER_LABEL} ${i18n.t('filters.prices.title_on')} ${numToWords(
        touristsCount,
      )}`}
      showClearBtn={isChanged}
      onClear={() => onClear(FILTER_NAME)}
      isMobile={isMobile}
    >
      <StyledPriceFilter
        key={`${minPrice}/${maxPrice}`}
        className="filter-prices"
        segments={isMobile ? null : segments}
        externalMin={minPrice}
        externalMax={maxPrice}
        onChange={handleChange}
        minBound={min}
        maxBound={max}
        isMobile={isMobile}
        currencyCode={currencyCode}
      />
    </FilterGroup>
  );
};

const StyledCurrencyFormat = styled(CurrencyFormat)`
  margin-right: 5px;
`;

const StyledPriceFilter = styled(PriceFilter)`
  height: 80px;
  font-size: 14px;

  input {
    height: 39px;
    background: transparent;
  }

  ${({ isMobile }) =>
    isMobile &&
    `
      width: auto;
      height: auto;
      font-size: 17px;

      input {
        height: 44px;
      }
    `}
`;

export default Object.assign(FilterPrices, {
  filterName: FILTER_NAME,
  filterLabel: FILTER_LABEL,
  valuesLabel: (data) => {
    const { minPrice, maxPrice }: PricesData = data;
    return (
      <>
        {minPrice ? (
          <StyledCurrencyFormat
            value={minPrice}
            prefix={`${i18n.t('filters.prices.from')}${NBSP}`}
          />
        ) : null}
        {maxPrice ? (
          <StyledCurrencyFormat
            value={maxPrice}
            prefix={`${i18n.t('filters.prices.before')}${NBSP}`}
          />
        ) : null}
      </>
    );
  },
});
